import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { SafeHtml, DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslateModule } from '@ngx-translate/core';
import { MaterialModule } from '../../../../core/models/material.module';
import { SanitizerService } from '../../../../core/security/sanitizer.service';

export interface AnnouncementDialogData {
  icon: any;
  title: string;
  message: string;
  confirmText?: string;
  file?: string | null;
  fileType?: 'image' | 'pdf' | null;
}

@Component({
  selector: 'app-announcement-dialog',
  standalone: true,
  imports: [MatDialogModule, MaterialModule, TranslateModule],
  template: `
    <div class="dialog">
      <div>
        <h2 mat-dialog-title>
          <mat-icon>{{ data.icon }}</mat-icon>
          {{ data.title }}
        </h2>
      </div>

      <mat-dialog-content>
        <!-- Rich Text Content -->
        <div class="announcement-content" [innerHTML]="safeMessage"></div>

        <!-- IMAGE PREVIEW -->
        <img
          *ngIf="data.file && (data.fileType === 'image' || getFileType(data.file) === 'image')"
          [src]="data.file"
          [class.popup-image]="!isImageExpanded"
          [class.expanded-image]="isImageExpanded"
          (click)="toggleImageExpansion()"
          alt="Preview"
          style="cursor: pointer;"
        />

        <!-- PDF PREVIEW (Small) -->
        <div
          *ngIf="data.file && (data.fileType === 'pdf' || getFileType(data.file) === 'pdf') && safePdfUrl && !isPdfExpanded"
          class="pdf-preview-wrapper"
          (click)="togglePdfExpansion()"
        >
          <iframe
            [src]="safePdfUrl"
            class="popup-pdf"
          ></iframe>
          <div class="click-to-expand">Click to expand</div>
        </div>

        <!-- PDF Overlay with Close Button and Expanded PDF -->
        <div *ngIf="isPdfExpanded" class="pdf-overlay" (click)="togglePdfExpansion()">
          <button class="overlay-close-btn" (click)="togglePdfExpansion(); $event.stopPropagation()">
            <mat-icon>close</mat-icon>
          </button>
          <iframe
            [src]="safePdfUrl"
            class="expanded-pdf"
            (click)="$event.stopPropagation()"
          ></iframe>
        </div>

        <!-- Image Overlay with Close Button -->
        <div *ngIf="isImageExpanded" class="image-overlay" (click)="toggleImageExpansion()">
          <button class="overlay-close-btn" (click)="toggleImageExpansion(); $event.stopPropagation()">
            <mat-icon>close</mat-icon>
          </button>
        </div>
      </mat-dialog-content>

      <mat-dialog-actions align="end">
        <button mat-raised-button mat-button (click)="close()" class="next_btn" *ngIf="data.confirmText">
          {{ data.confirmText || 'OK' | translate }}
        </button>
      </mat-dialog-actions>
    </div>
  `,
styles: [`
    mat-dialog-actions {
      display: flex;
      justify-content: center !important;
    }

    ::ng-deep .mdc-dialog__title {
      text-align: center !important;
      font-family: var(--main-font) !important;
      color: var(--primary-color) !important;
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      margin-top: 10px;
      font-weight: 600 !important;
    }

    .mat-mdc-dialog-title::before {
      display: none;
    }

    .mat-icon {
      padding-bottom: 10px;
      color: var(--primary-color);
      width: 30px;
      height: auto;
      font-size: 30px;
      margin-left: -10px;
    }

    .next_btn {
      width: 160px !important;
    }

    .dialog {
      padding: 0px 8px !important;
    }

    /* ========== OVERRIDE ALL P MARGINS ========== */
    ::ng-deep .announcement-content p,
    ::ng-deep mat-dialog-content p,
    ::ng-deep .mat-mdc-dialog-content p {
      margin-bottom: 0px !important;
      margin-top: 0px !important;
      padding: 0px !important;
    }

    /* ========== ANNOUNCEMENT CONTENT STYLES ========== */
    .announcement-content {
      font-family: var(--main-font) !important;
      // font-size: 16px;
      line-height: 1.4;
      color: #333;
      text-align: left !important;
    }

    .announcement-content * {
      font-family: var(--main-font) !important;
    }

    /* Preserve Quill/Rich text formatting - Remove all margins and padding */
    .announcement-content p {
      margin: 0px !important;
      padding: 0px !important;
    }

    /* Handle Quill editor alignment classes */
    .announcement-content .ql-align-left,
    .announcement-content p.ql-align-left {
      text-align: left !important;
    }

    .announcement-content .ql-align-center,
    .announcement-content p.ql-align-center,
    .announcement-content h1.ql-align-center,
    .announcement-content h2.ql-align-center,
    .announcement-content h3.ql-align-center {
      text-align: center !important;
    }

    .announcement-content .ql-align-right,
    .announcement-content p.ql-align-right {
      text-align: right !important;
    }

    .announcement-content .ql-align-justify,
    .announcement-content p.ql-align-justify {
      text-align: justify !important;
    }

    .announcement-content strong {
      font-weight: 700;
    }

    .announcement-content ul {
      padding-left: 20px;
      margin: 0px !important;
    }

    .announcement-content ol {
      padding-left: 20px;
      margin: 0px !important;
    }

    .announcement-content li {
      margin: 0px !important;
    }

    .announcement-content h1,
    .announcement-content h2,
    .announcement-content h3 {
      margin: 16px 0 8px 0 !important;
      font-weight: 600;
    }

    .announcement-content h1 {
      font-size: 24px;
    }

    .announcement-content h2 {
      font-size: 20px;
    }

    .announcement-content h3 {
      font-size: 18px;
    }

    /* Remove extra spacing from empty paragraphs */
    .announcement-content p:empty {
      display: block;
      height: 8px; /* Small gap for visual separation */
    }

    /* ========== IMAGE & PDF PREVIEW STYLES ========== */
    .popup-image {
      width: 100%;
      max-height: 55vh;
      height: auto;
      display: block;
      margin: 0 auto;
      border-radius: 8px;
      box-shadow: none;
      object-fit: contain;
      transition: transform 0.3s ease;
    }

    .expanded-image {
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      max-width: 90vw;
      max-height: 90vh;
      width: auto;
      height: auto;
      object-fit: contain;
      z-index: 10001;
      border-radius: 8px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
      cursor: zoom-out;
      background: transparent;
    }

    .popup-pdf {
      width: 100%;
      height: 300px;
      border: none;
      border-radius: 8px;
      margin: 20px 0;
      pointer-events: none;
    }

    .pdf-preview-wrapper {
      position: relative;
      cursor: pointer;
      transition: all 0.3s ease;
    }

    .pdf-preview-wrapper:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      transform: scale(1.01);
    }

    .click-to-expand {
      position: absolute;
      bottom: 30px;
      left: 50%;
      transform: translateX(-50%);
      background: rgba(0, 0, 0, 0.7);
      color: white;
      padding: 8px 16px;
      border-radius: 4px;
      font-size: 14px;
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.3s ease;
    }

    .pdf-preview-wrapper:hover .click-to-expand {
      opacity: 1;
    }

    .expanded-pdf {
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 90vw;
      height: 90vh;
      z-index: 10001;
      border-radius: 8px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
      background: white;
      border: none;
      pointer-events: auto;
    }

    /* Overlay for PDF and Image */
    .pdf-overlay,
    .image-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.8);
      z-index: 10000;
      cursor: zoom-out;
      animation: fadeIn 0.3s ease;
    }

    @keyframes fadeIn {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }

    /* Close button on overlay */
    .overlay-close-btn {
      position: fixed;
      top: 20px;
      right: 30px;
      z-index: 10002;
      background: white;
      border: none;
      border-radius: 50%;
      width: 45px;
      height: 45px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
      transition: all 0.3s ease;
    }

    .overlay-close-btn:hover {
      background: #f5f5f5;
      transform: scale(1.1);
    }

    .overlay-close-btn mat-icon {
      color: #333;
      font-size: 28px;
      width: 28px;
      height: 28px;
      margin: 0;
      padding: 0;
    }

    @media (max-width: 768px) {
      ::ng-deep .mat-mdc-dialog-inner-container {
        overflow: auto !important;
        max-height: 800px !important;
      }

      .announcement-content {
        max-height: 50vh;
      }
    }

    ::ng-deep .announcement-content ul,
::ng-deep .announcement-content ol,
::ng-deep .announcement-content dl {
  margin-top: 0;
    margin-bottom: 0px;
 
}
   ::ng-deep .announcement-content h1,
::ng-deep .announcement-content h3 {
  margin-top: 0;

  margin-bottom: 0px !important;
}
      
  `]
})
export class AnnouncementDialogComponent implements OnInit {
  safeMessage!: SafeHtml;
  safePdfUrl?: SafeResourceUrl;
  isImageExpanded = false;
  isPdfExpanded = false;

  constructor(
    public dialogRef: MatDialogRef<AnnouncementDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AnnouncementDialogData,
    private sanitizer: DomSanitizer,
    private sanitizerService: SanitizerService
  ) {
    // Convert HTML string to safe HTML using allowlist sanitizer
    this.safeMessage = this.sanitizerService.sanitizeHtml(data.message);
  }

  ngOnInit() {
    // Sanitize PDF URL if needed using allowlisted resource sanitizer
    if (this.data.file && (this.data.fileType === 'pdf' || this.getFileType(this.data.file) === 'pdf')) {
      const viewerUrl = `https://docs.google.com/gview?url=${encodeURIComponent(this.data.file)}&embedded=true`;
      this.safePdfUrl = this.sanitizerService.sanitizeResourceUrl(viewerUrl);
    }
  }

  toggleImageExpansion(): void {
    this.isImageExpanded = !this.isImageExpanded;

    if (this.isImageExpanded) {
      document.body.style.overflow = 'hidden';
    } else {
      setTimeout(() => {
        document.body.style.overflow = '';
      }, 300);
    }
  }

  togglePdfExpansion(): void {
    this.isPdfExpanded = !this.isPdfExpanded;

    if (this.isPdfExpanded) {
      document.body.style.overflow = 'hidden';
    } else {
      setTimeout(() => {
        document.body.style.overflow = '';
      }, 300);
    }
  }

  ngOnDestroy(): void {
    document.body.style.overflow = '';
  }

  close() {
    this.dialogRef.close(true);
  }

  getFileType(fileName: string | undefined): 'image' | 'pdf' | null {
    if (!fileName) return null;

    const ext = fileName.toLowerCase().split('.').pop();

    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext || '')) {
      return 'image';
    }
    if (ext === 'pdf') {
      return 'pdf';
    }
    return null;
  }
}